import React, {Component, ReactNode, RefObject} from 'react'
import {PlainStyledButton, UnstyledLinkStyledButton} from '../../../styledComponents/Button'
import styled from 'styled-components'
import {MeetingCommentFile} from '../model/MeetingCommentFile'
import {Colors, FontSize, FontWeights} from '../../../styledComponents/Constants'
import {FlexContainer, FullWidthLeftRowFlexContainer, RowFlexContainer} from '../../../styledComponents/FlexComponents'
import {getLinkIconComponent, IconBox} from '../AssocDirectorCommon'
import {getFileExtension} from '../../../__NoAutomatedTests/common/util/Natutil'
import {faFileMedical, faTimes} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import { Box, OutlinedInput } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import {AttachmentFileName} from './MeetingCommentComponent'
import {HKConstants} from '../../../HKConstants'
import {HideInPC, HideInSP} from '../../../styledComponents/ResponsiveConvenience'
import {StyledDiv} from '../../../styledComponents/StyledTypeDefinitions'

export interface InputError {
    title: string,
    messages: string[],
    fileNameList?: string[],
}

interface Props {
    inDialog?: boolean
    submitButtonLabel: string
    onClickSubmitButton: () => Promise<void>
    onClickCancelButton?: () => void
    onChange: (
        content: string,
        commentFileList: MeetingCommentFile[],
        deleteFileNumberList: number[],
        inputError?: InputError,
    ) => void
    content: string
    placeholder?: string
    commentFileList: MeetingCommentFile[]
    deleteFileNumberList: number[]
    inputError?: InputError
}

class MeetingCommentInputForm extends Component<Props> {
    commentFileRef: RefObject<HTMLInputElement>

    constructor(props: Readonly<Props>) {
        super(props)

        this.commentFileRef = React.createRef()

        this.handleChangeComment = this.handleChangeComment.bind(this)
        this.handleClickAddFileButton = this.handleClickAddFileButton.bind(this)
        this.handleChangeFileInput = this.handleChangeFileInput.bind(this)
        this.handleClickDeleteFileButton = this.handleClickDeleteFileButton.bind(this)
    }

    handleChangeComment(event: React.ChangeEvent<HTMLTextAreaElement>): void {
        this.props.onChange(
            event.target.value,
            this.props.commentFileList,
            this.props.deleteFileNumberList,
            this.props.inputError,
        )
    }

    handleClickAddFileButton(): void {
        const fileUpload = document.getElementById('fileUpload-' + this.props.submitButtonLabel) as HTMLInputElement
        if (fileUpload) {
            fileUpload.click()
        }
    }

    handleChangeFileInput(event: React.ChangeEvent<HTMLInputElement>): void {
        if (event.target.files && event.target.files[0] && event.target.files[0].name) {
            const {commentFileList} = this.props
            const fileName = event.target.files[0].name
            const fileSize = event.target.files[0].size
            let inputError: InputError | undefined
            if (fileSize > HKConstants.VMN.ASO.MAX_COMMENT_FILE_SIZE * 1024 * 1024) {
                inputError = {
                    title: '資料を追加できませんでした。',
                    messages: [
                        'アップロードの上限サイズ(20MB)を超えています。資料のサイズを確認してください。',
                    ],
                    fileNameList: [`${fileName}(${Math.floor(fileSize / (1024 * 1024))}MB)`],
                }
            }
            if (fileSize === 0) {
                inputError = {
                    title: '資料を追加できませんでした。',
                    messages: [
                        '空の資料は追加することができません。資料内容を確認してください。',
                    ],
                    fileNameList: [`${fileName}(0バイト)`],
                }
            }
            if (inputError) {
                this.props.onChange(
                    this.props.content,
                    commentFileList,
                    this.props.deleteFileNumberList,
                    inputError,
                )
                if (this.commentFileRef.current) {
                    this.commentFileRef.current.value = ''
                }
                return
            }
            const reader: FileReader = new FileReader()
            reader.onload = () => {
                commentFileList.push({
                    fileNumber: null,
                    fileName: fileName,
                    fileSize: fileSize,
                    fileData: String(reader.result),
                })
                this.props.onChange(
                    this.props.content,
                    commentFileList,
                    this.props.deleteFileNumberList,
                    this.props.inputError,
                )
            }
            reader.onerror = () => {
                this.props.onChange(
                    this.props.content,
                    commentFileList,
                    this.props.deleteFileNumberList,
                    {
                        title: '資料を追加できませんでした。',
                        messages: ['資料の読み込みに失敗しました。もう一度資料を選択してください。'],
                        fileNameList: [`${fileName}`],
                    },
                )
            }
            reader.readAsDataURL(event.target.files[0])
        }
        if (this.commentFileRef.current) {
            this.commentFileRef.current.value = ''
        }
    }

    handleClickDeleteFileButton(deleteIndex: number): void {
        const {commentFileList, deleteFileNumberList} = this.props
        const newCommentFileList: MeetingCommentFile[] = []
        const newDeleteFileNumberList: number[] = [...deleteFileNumberList]

        for (let index = 0; index < commentFileList.length; index++) {
            const commentFile = commentFileList[index]
            if (index !== deleteIndex) {
                newCommentFileList.push(commentFile)
            } else if (commentFile.fileNumber) {
                newDeleteFileNumberList.push(commentFile.fileNumber)
            }
        }

        this.props.onChange(
            this.props.content,
            newCommentFileList,
            newDeleteFileNumberList,
            this.props.inputError,
        )
    }

    renderCommentInputAreaScrollAll(): ReactNode {
        const {
            content,
            placeholder,
            commentFileList,
        } = this.props
        return <>
            <ScrollCommentInputArea>
                <BetweenColumnFlexContainer>
                    <StyledOutlinedInput
                        inputProps={{
                            minLength: 0,
                            maxLength: 2000,
                        }}
                        value={content}
                        fullWidth
                        multiline
                        autoFocus
                        placeholder={placeholder}
                        onChange={this.handleChangeComment}
                    />
                    <FileColumnFlexContainer>
                        {
                            commentFileList &&
                            commentFileList.map((commentFile, index) => {
                                return <AttachmentFile key={`commentFile-${index}`}>
                                    <RowFlexContainer>
                                        <FullWidthLeftRowFlexContainer>
                                            <AttachmentFileIconBox>
                                                {getLinkIconComponent(getFileExtension(commentFile.fileName))}
                                            </AttachmentFileIconBox>
                                            <AttachmentFileName>{commentFile.fileName}</AttachmentFileName>
                                            <UnstyledLinkStyledButton
                                                id={`fileDeleteButton-${index}`}
                                                onClick={() => this.handleClickDeleteFileButton(index)}
                                            >
                                                <FontAwesomeIcon icon={faTimes}/>
                                            </UnstyledLinkStyledButton>
                                        </FullWidthLeftRowFlexContainer>
                                    </RowFlexContainer>
                                </AttachmentFile>
                            })
                        }
                    </FileColumnFlexContainer>
                </BetweenColumnFlexContainer>
            </ScrollCommentInputArea>
        </>
    }

    renderCommentInputAreaScrollText(): ReactNode {
        const {
            inDialog,
            content,
            placeholder,
            commentFileList,
        } = this.props
        return <>
            <CommentInputArea>
                <StyledOutlinedInput
                    inputProps={{
                        minLength: 0,
                        maxLength: 2000,
                    }}
                    minRows={inDialog ? 6.5 : 3}
                    maxRows={inDialog ? 6.5 : 4.5}
                    value={content}
                    fullWidth
                    multiline
                    placeholder={placeholder}
                    onChange={this.handleChangeComment}
                />
                {
                    commentFileList &&
                    commentFileList.map((commentFile, index) => {
                        return <AttachmentFile key={`commentFile-${index}`}>
                            <RowFlexContainer>
                                <FullWidthLeftRowFlexContainer>
                                    <AttachmentFileIconBox>
                                        {getLinkIconComponent(getFileExtension(commentFile.fileName))}
                                    </AttachmentFileIconBox>
                                    <AttachmentFileName>{commentFile.fileName}</AttachmentFileName>
                                    <UnstyledLinkStyledButton
                                        id={`fileDeleteButton-${index}`}
                                        onClick={() => this.handleClickDeleteFileButton(index)}
                                    >
                                        <FontAwesomeIcon icon={faTimes}/>
                                    </UnstyledLinkStyledButton>
                                </FullWidthLeftRowFlexContainer>
                            </RowFlexContainer>
                        </AttachmentFile>
                    })
                }
            </CommentInputArea>
        </>
    }

    render(): ReactNode {
        const {
            content,
            commentFileList,
            submitButtonLabel,
            inDialog,
            onClickSubmitButton,
            onClickCancelButton,
            inputError,
        } = this.props
        const disabledAddFileButton = commentFileList.length >= 5
        const isSubmitDisabled = content.length === 0
        return <>
            <StyledFlexContainer>
                {
                    inputError &&
                    <ErrorMessageArea>
                        <ErrorMessageTitle>
                            {inputError.title}
                        </ErrorMessageTitle>
                        <Box height={'5px'}/>
                        {inputError.messages.map((message, index) =>
                            <ErrorMessage key={'errorMessage-' + index}>
                                {message}
                            </ErrorMessage>)}
                        {
                            inputError.fileNameList &&
                            inputError.fileNameList.length > 0 && <>
                                <Box height={'10px'}/>
                                {
                                    inputError.fileNameList.map((fileName, index) =>
                                        <ErrorMessage key={'errorFile-' + index}>
                                            {fileName}
                                        </ErrorMessage>)
                                }
                            </>
                        }
                    </ErrorMessageArea>
                }
                <HideInSP>
                    {inDialog ? this.renderCommentInputAreaScrollAll() : this.renderCommentInputAreaScrollText()}
                </HideInSP>
                <HideInPC>
                    {this.renderCommentInputAreaScrollText()}
                </HideInPC>
                <Box height={'15px'}/>
                <RowFlexContainer>
                    <RowFlexContainer>
                        <Box ml={'5px'}>
                            <AddFileButton
                                id={'addFileButton'}
                                onClick={this.handleClickAddFileButton}
                                disabled={disabledAddFileButton}
                            >
                                <FontAwesomeIcon icon={faFileMedical} size={'lg'}/>
                            </AddFileButton>
                        </Box>
                        <DisplayNoneFileInput
                            type={'file'}
                            id={'fileUpload-' + submitButtonLabel}
                            ref={this.commentFileRef}
                            onChange={this.handleChangeFileInput}
                        />
                    </RowFlexContainer>
                    <RowFlexContainer>
                        {
                            onClickCancelButton &&
                            <Box mr={'20px'}>
                                <UnstyledLinkStyledButton onClick={onClickCancelButton}>
                                    キャンセル
                                </UnstyledLinkStyledButton>
                            </Box>
                        }
                        <SubmitPrimaryButton
                            onClick={onClickSubmitButton}
                            disabled={isSubmitDisabled}
                        >
                            {submitButtonLabel}
                        </SubmitPrimaryButton>
                    </RowFlexContainer>
                </RowFlexContainer>
            </StyledFlexContainer>
        </>
    }
}

export default MeetingCommentInputForm

const DisplayNoneFileInput = styled.input`
  display: none;
`

const AttachmentFile = styled.div`
  margin-top: 5px;
  padding: 5px 10px;
  border: 1px solid ${Colors.AccentLight};
  border-radius: 4px;
  background-color: ${Colors.Surface};
`

const AttachmentFileIconBox = styled(IconBox)`
  color: ${Colors.AccentDark};
`

const StyledFlexContainer = styled(FlexContainer)`
  flex-direction: column;
  justify-content: flex-start;
  height: 100%;
  width: 100%;
`

const StyledOutlinedInput = withStyles({
    root: {
        fontSize: FontSize.Body,
        color: Colors.Body,
        padding: 0,
        alignItems: 'flex-start',
    },
    input: {
        fontFamily: '"Noto Sans JP", sans-serif',
        lineHeight: 1.5,
    },
    notchedOutline: {
        border: 'none',
    },
})
(OutlinedInput)

const CommentInputArea = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${Colors.Surface};
  border: ${Colors.AccentLight} 1px solid;
  border-radius: 4px;
  padding: 10px;
  box-sizing: border-box;
  width: 100%;
`

const ScrollCommentInputArea = styled(CommentInputArea)`
  height: 260px;
  overflow: scroll;
`

export const ErrorMessageArea = styled.div`
  background: #fbebec;
  padding: 10px;
  margin-bottom: 15px;
`

export const ErrorMessageTitle = styled.div`
  color: ${Colors.Alert};
  font-size: ${FontSize.Body};
  font-weight: ${FontWeights.Bold};
  line-height: 24px;
`

export const ErrorMessage = styled.div`
  color: ${Colors.Body};
  font-size: ${FontSize.Middle};
  line-height: 21px;
`

const AddFileButton = styled(UnstyledLinkStyledButton)`
  color: ${Colors.AccentDark};

  :disabled {
    cursor: default;
    color: ${Colors.AccentLight};
  }
`

const SubmitPrimaryButton = styled(PlainStyledButton)`
  color: ${Colors.Surface};
  background-color: ${Colors.Primary};

  border: none;
  border-radius: 4px;
  line-height: 1.5;
  padding: 8px 15px;

  :active {
    background-color: ${Colors.PrimaryActive};
    box-shadow: none;
  }

  :disabled {
    pointer-events: none;
    border: none;
    background-color: ${Colors.AccentLight};
    box-shadow: none;
  }
`

const BetweenColumnFlexContainer: StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 250px;
`

const FileColumnFlexContainer: StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 10px;
`
